import { Component, OnDestroy, OnInit, HostBinding, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';

import { LayoutService } from '@app/shared/services/layout.service';
import { UserInfo } from '@app/shared/models/auth/user-info.model';

import { SidebarMenuItem } from '@app/modules/common-features/sidebar/models/sidebar-menu-item.model';
import { SidebarMenuContainer } from '@app/modules/common-features/sidebar/models/sidebar-menu-container.model';
import { ISidebarComponent } from '@app/shared/interfaces/sidebar-component.interface';
import { TokenStorageService, TopNavBarService } from '@app/shared/services';

import { ASidebarBuilder } from './builders/a-sidebar-builder';
import { SidebarBuilderFactory } from './builders/sidebar-builder-factory';
import { NavNotificationsService } from '@api/api-services/nav-notifications.service';
import { NotificationCounterService } from '@app/shared/services/notification-counter.service';
import { NotificationsCounterApiModel } from '@api/api-models';

@Component({
  selector: 'a360-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy, ISidebarComponent {
  private subs = new Array<Subscription>();
  private sidebarBuilder: ASidebarBuilder;

  public get programSelectorContent(): Array<SidebarMenuItem> {
    return this.sidebarBuilder.programSelectorContent;
  }

  public get programMenuContent(): Array<SidebarMenuItem | SidebarMenuContainer> {
    return this.sidebarBuilder.programMenuContent;
  }

  public get systemMenuContent(): Array<SidebarMenuItem | SidebarMenuContainer> {
    return this.sidebarBuilder.systemMenuContent;
  }

  public get utilsMenuContent(): Array<SidebarMenuItem | SidebarMenuContainer> {
    return this.sidebarBuilder.utilsMenuContent;
  }

  public notificationItem: SidebarMenuItem = new SidebarMenuItem({
    name: 'Notifications',
    label: 'Notifications',
    icon: 'bell',
    action: () => {
      this.toggleNotifications();
    }
  });

  public counters: NotificationsCounterApiModel;

  public get platformTopNavEnabled(): boolean {
    return this.tokenStorageService.userInfo?.CurrentSite.PlatformTopNavEnabled ?? false;
  }

  constructor(
    private readonly tokenStorageService: TokenStorageService,
    public readonly layoutService: LayoutService,
    private readonly sidebarBuilderFactory: SidebarBuilderFactory,
    private readonly notificationsService: NavNotificationsService,
    private readonly notificationCounterService: NotificationCounterService,
    private readonly topnavBarService: TopNavBarService
  ) {
    this.subs.push(this.tokenStorageService.userInfoChanged.subscribe(this.initBuilder.bind(this)));
  }

  public ngOnInit(): void {
    this.subs.push(
      this.notificationCounterService.counters$.subscribe((counters) => {
        this.counters = counters;
      })
    );

    this.layoutService.sidebarComponent = this;

    if (this.tokenStorageService.userInfo) {
      this.initBuilder(this.tokenStorageService.userInfo);
    }

    this.subs.push(
      this.notificationsService.subscribeNotificationStateActionObserver().subscribe((model) => {
        this.notificationCounterService.handleNotificationStateAction(model);
      })
    );

    this.notificationItem.action = this.toggleNotifications.bind(this);
  }

  public ngOnDestroy(): void {
    this.subs.forEach((x) => x.unsubscribe());
  }

  public closeAllContainers(): void {
    this.programMenuContent
      .concat(this.systemMenuContent)
      .filter((s) => s instanceof SidebarMenuContainer)
      .map((s) => s as SidebarMenuContainer)
      .forEach((x) => (x.expanded = false));
  }

  @HostBinding('class.disabled')
  public get disabled(): boolean {
    return this.layoutService.barsDisabled;
  }

  private initBuilder(userInfo: UserInfo): void {
    this.sidebarBuilder = this.sidebarBuilderFactory.get(userInfo);
    this.sidebarBuilder.rebuild(userInfo);
  }

  public toggleNotifications(): void {
    this.notificationsService.emitNotificationsStateObserver(!this.notificationsService.isNotificationsOpen);
    this.layoutService.toggleSidebar();
    this.topnavBarService.setSidebarOpenCloseState();
  }
}
